import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import SumsubWebSdk from '@sumsub/websdk-react';
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import {
  completeOnboarding,
  fetchIdentityToken,
  refreshIdentityStatus,
} from '../api/client.js';
import { StepPaper } from './StepPaper.js';
import type { WizardStepProps } from './WizardRouter.js';

/**
 * Identity step UX has three discrete phases:
 *
 *   kyc        - the Sumsub Web SDK iframe is mounted and the user is filling
 *                in documents / liveness.
 *   finalising - the user has submitted in the SDK (or their identity was
 *                already submitted on a previous visit). We unmount the
 *                iframe so they never see Sumsub's "You may close this page"
 *                message, show our own spinner, and poll the backend (which
 *                pulls from Sumsub). The moment status flips to submitted /
 *                approved we call /complete and advance to step 7.
 *   rejected   - Sumsub returned RED. Tell the user to contact us; no auto
 *                advance.
 */
type Phase = 'kyc' | 'finalising' | 'rejected';

const initialPhase = (status: string): Phase => {
  if (status === 'rejected') return 'rejected';
  if (status === 'submitted' || status === 'approved') return 'finalising';
  return 'kyc';
};

export const Step6Identity = ({ guid, data, onAdvance, onPatched }: WizardStepProps) => {
  const [phase, setPhase] = useState<Phase>(() => initialPhase(data.identity.status));

  const tokenQuery = useQuery({
    queryKey: ['sumsub-token', guid],
    queryFn: () => fetchIdentityToken(guid),
    staleTime: 0,
    enabled: phase === 'kyc',
  });

  // Pull-based status sync: while we're waiting for Sumsub confirmation, hit
  // /identity/refresh every 2s. The query stops automatically once we see a
  // terminal state. This means we don't depend on the webhook for the wizard
  // to advance, which is essential in local dev (no public tunnel) and a
  // useful fallback in prod against webhook delivery hiccups.
  const refreshQuery = useQuery({
    queryKey: ['identity-refresh', guid],
    queryFn: () => refreshIdentityStatus(guid),
    enabled: phase === 'finalising',
    refetchInterval: (q) => {
      const status = q.state.data?.identity.status;
      if (status === 'submitted' || status === 'approved' || status === 'rejected') {
        return false;
      }
      return 2000;
    },
    refetchIntervalInBackground: true,
  });

  const completeMutation = useMutation({
    mutationFn: () => completeOnboarding(guid),
    onSuccess: (next) => {
      // /complete already set currentStep=7 and status=completed server-side,
      // so we just patch the local cache and let the WizardRouter render
      // Step7 from the new currentStep. We deliberately do NOT call
      // onAdvance(7) here - that would PATCH /step right after /complete and
      // we'd send a redundant round trip for no UI benefit.
      onPatched(next);
    },
  });

  const runComplete = useCallback(() => {
    if (completeMutation.isPending || completeMutation.isSuccess) return;
    completeMutation.mutate();
  }, [completeMutation]);

  // React to status transitions while finalising (covers both the live poll
  // result and the initial-load case where the user is resuming after having
  // already submitted).
  useEffect(() => {
    if (phase !== 'finalising') return;
    const status = refreshQuery.data?.identity.status ?? data.identity.status;
    if (status === 'rejected') {
      setPhase('rejected');
    } else if (status === 'submitted' || status === 'approved') {
      runComplete();
    }
  }, [phase, refreshQuery.data, data.identity.status, runComplete]);

  const handleSdkMessage = useCallback((type: string, payload?: unknown) => {
    // `onApplicantStatusChanged` is what the SDK fires on *resume* of an
    // already-finished applicant (the original submit event isn't replayed
    // when the iframe re-mounts). The payload carries `reviewStatus`; we
    // ignore the harmless 'init' state that fires during normal SDK boot.
    if (type === 'idCheck.onApplicantStatusChanged') {
      const status = (payload as { reviewStatus?: string } | undefined)?.reviewStatus;
      if (status && status !== 'init') {
        setPhase('finalising');
      }
    }
  }, []);

  return (
    <StepPaper>
      <Stack spacing={3}>
        <Typography variant="h3">Verify your identity</Typography>

        {phase === 'kyc' && (
          <>
            <Typography variant="body1" color="text.secondary">
              To activate your ScriptPad account, we need to verify your identity. Please take a photo of
              your identification document and use your phone camera to confirm your face matches the
              document.
            </Typography>

            {tokenQuery.isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            )}

            {tokenQuery.isError && (
              <Alert severity="error">
                Could not initialise identity verification:{' '}
                {(tokenQuery.error as Error).message ?? 'unknown error'}
              </Alert>
            )}

            {tokenQuery.data && (
              <Box sx={{ minHeight: 480 }}>
                <SumsubWebSdk
                  accessToken={tokenQuery.data.token}
                  expirationHandler={async () => {
                    const fresh = await fetchIdentityToken(guid);
                    return fresh.token;
                  }}
                  config={{ lang: 'en', theme: 'light' }}
                  onMessage={handleSdkMessage}
                  onError={(err: unknown) => {
                    console.error('Sumsub error', err);
                  }}
                />
              </Box>
            )}

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2 }}
            >
              <Button onClick={() => onAdvance(5)} variant="text">
                Back
              </Button>
            </Stack>
          </>
        )}

        {phase === 'finalising' && (
          <Stack
            spacing={3}
            alignItems="center"
            sx={{ py: { xs: 6, sm: 10 }, textAlign: 'center' }}
          >
            <CircularProgress size={56} thickness={4} />
            <Stack spacing={1} alignItems="center" sx={{ maxWidth: 480 }}>
              <Typography variant="h5">Finalising your verification</Typography>
              <Typography variant="body1" color="text.secondary">
                Hang tight - we're confirming your details with our identity provider. This usually only
                takes a few seconds. Please don't close this page.
              </Typography>
            </Stack>

            {(refreshQuery.isError || completeMutation.isError) && (
              <Alert
                severity="error"
                sx={{ width: '100%', maxWidth: 480, textAlign: 'left' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (completeMutation.isError) {
                        completeMutation.reset();
                        runComplete();
                      } else {
                        refreshQuery.refetch();
                      }
                    }}
                  >
                    Retry
                  </Button>
                }
              >
                {(completeMutation.error as Error)?.message ??
                  (refreshQuery.error as Error)?.message ??
                  'Unknown error'}
              </Alert>
            )}
          </Stack>
        )}

        {phase === 'rejected' && (
          <Stack spacing={2} sx={{ py: { xs: 4, sm: 6 } }}>
            <Alert severity="error">
              Unfortunately we weren't able to verify your identity. Please contact the ScriptPad team
              so we can help you complete your onboarding.
            </Alert>
            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2 }}
            >
              <Button onClick={() => onAdvance(5)} variant="text">
                Back
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </StepPaper>
  );
};
